import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, Loader2, RotateCcw, Download, Settings, HelpCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';

const AIChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationSessions, setConversationSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const quickActions = [
    { label: "Request Time Off", prompt: "I need to request time off" },
    { label: "Find Employee", prompt: "Find employee information for" },
    { label: "Record Observation", prompt: "I want to record a classroom observation" },
    { label: "Check Approvals", prompt: "Show me pending approvals" },
    { label: "Absence Statistics", prompt: "Show me absence statistics for this month" },
    { label: "Help", prompt: "What can you help me with?" }
  ];

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation sessions and history on mount
  useEffect(() => {
    loadConversationSessions();
    loadConversationHistory();
    // Focus input on mount
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const loadConversationSessions = async () => {
    try {
      const response = await apiService.makeRequest('/api/ai/conversations/sessions');
      setConversationSessions(response.sessions || []);
    } catch (error) {
      console.error('Failed to load conversation sessions:', error);
    }
  };

  const loadConversationHistory = async (sessionId = null) => {
    try {
      setIsLoading(true);
      const params = sessionId ? `?session_id=${sessionId}&limit=50` : '?limit=50';
      const response = await apiService.makeRequest(`/api/ai/conversations/history${params}`);
      
      if (response.history && response.history.length > 0) {
        const formattedMessages = response.history.map(msg => ({
          id: msg.id || Date.now() + Math.random(),
          type: msg.role === 'user' ? 'user' : 'ai',
          content: msg.content,
          timestamp: new Date(msg.timestamp || Date.now()),
          actions: msg.actions || []
        }));
        setMessages(formattedMessages);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to load conversation history:', error);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await apiService.makeRequest('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: message,
          context: sessionId ? { session_id: sessionId } : {}
        })
      });

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.response,
        timestamp: new Date(),
        actions: response.actions || []
      };

      setMessages(prev => [...prev, aiMessage]);
      setSessionId(response.session_id);
      setCurrentSessionId(response.session_id);

      // Reload sessions to show the new/updated session
      loadConversationSessions();

    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: "I'm sorry, I encountered an error processing your request. Please try again or contact support.",
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      toast({
        title: 'Chat Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuickAction = (prompt) => {
    sendMessage(prompt);
  };

  const handleActionClick = async (action) => {
    try {
      switch (action.type) {
        case 'create_absence':
          toast({
            title: 'Absence Request Created',
            description: 'Your time off request has been submitted for approval.'
          });
          break;
        case 'show_employee':
          toast({
            title: 'Employee Information',
            description: `Showing details for ${action.data?.name ?? 'employee'}`
          });
          break;
        case 'create_observation':
          toast({
            title: 'Observation Recorded',
            description: 'Your observation has been saved successfully.'
          });
          break;
        default:
          console.log('Unhandled action:', action);
      }
    } catch (error) {
      console.error('Action failed:', error);
      toast({
        title: 'Action Failed',
        description: 'Failed to complete the requested action.',
        variant: 'destructive'
      });
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setSessionId(null);
    setCurrentSessionId(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const loadSession = (session) => {
    setCurrentSessionId(session.session_id);
    setSessionId(session.session_id);
    loadConversationHistory(session.session_id);
  };

  const exportConversation = () => {
    const conversationText = messages.map(msg => 
      `[${msg.timestamp.toLocaleString()}] ${msg.type === 'user' ? 'You' : 'Assistant'}: ${msg.content}`
    ).join('\n\n');
    
    const blob = new Blob([conversationText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Conversation Exported',
      description: 'Your conversation has been downloaded as a text file.'
    });
  };

  return (
    <div className="h-full flex">
      {/* Sidebar with conversation sessions */}
      <div className="w-80 border-r bg-gray-50 flex flex-col">
        <div className="p-4 border-b bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">AI Assistant</h3>
            <Button
              onClick={startNewConversation}
              size="sm"
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              New Chat
            </Button>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            {quickActions.slice(0, 6).map((action, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                onClick={() => handleQuickAction(action.prompt)}
                className="text-xs h-8 justify-start"
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>

        <ScrollArea className="flex-1 p-4">
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Conversations</h4>
            {conversationSessions.length === 0 ? (
              <p className="text-sm text-gray-500">No previous conversations</p>
            ) : (
              conversationSessions.map((session) => (
                <div
                  key={session.session_id}
                  onClick={() => loadSession(session)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    currentSessionId === session.session_id
                      ? 'bg-yellow-100 border border-yellow-300'
                      : 'bg-white hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {session.last_message || 'New conversation'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(session.last_activity).toLocaleDateString()}
                  </p>
                  {session.message_count && (
                    <Badge variant="secondary" className="mt-1 text-xs">
                      {session.message_count} messages
                    </Badge>
                  )}
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center mr-3">
                <Bot className="w-6 h-6 text-black" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">PeoplePulse Assistant</h2>
                <p className="text-sm text-gray-500">AI-powered HR assistant</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {messages.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportConversation}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={startNewConversation}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                New Chat
              </Button>
            </div>
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                <span className="ml-3 text-gray-500">Loading conversation...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center max-w-2xl mx-auto">
                <div className="w-20 h-20 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mb-6">
                  <Bot className="w-10 h-10 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Welcome to PeoplePulse Assistant
                </h3>
                <p className="text-gray-600 mb-8 text-lg">
                  I can help you with time off requests, employee information, observations, approvals, and more. 
                  Just ask me in natural language!
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-lg">
                  {quickActions.map((action, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      onClick={() => handleQuickAction(action.prompt)}
                      className="p-4 h-auto text-left justify-start"
                    >
                      <div>
                        <div className="font-medium">{action.label}</div>
                        <div className="text-xs text-gray-500 mt-1">"{action.prompt}"</div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto">
                <MessageList 
                  messages={messages} 
                  isTyping={isTyping} 
                  onActionClick={handleActionClick}
                />
              </div>
            )}
            <div ref={messagesEndRef} />
          </ScrollArea>
        </div>

        {/* Input area */}
        <div className="p-4 border-t bg-white">
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
            <div className="flex space-x-4">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask me about time off, employee info, observations, or anything else..."
                disabled={isTyping}
                className="flex-1 text-base py-3"
              />
              <Button 
                type="submit" 
                disabled={isTyping || !inputValue.trim()}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-6"
              >
                {isTyping ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const MessageList = ({ messages, isTyping, onActionClick }) => {
  return (
    <div className="space-y-6">
      {messages.map((message) => (
        <MessageBubble 
          key={message.id} 
          message={message} 
          onActionClick={onActionClick}
        />
      ))}
      {isTyping && <TypingIndicator />}
    </div>
  );
};

const MessageBubble = ({ message, onActionClick }) => {
  const isUser = message.type === 'user';
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-2xl px-6 py-4 rounded-2xl ${
        isUser 
          ? 'bg-yellow-500 text-black ml-12' 
          : message.isError 
            ? 'bg-red-50 text-red-800 border border-red-200 mr-12'
            : 'bg-gray-100 text-gray-900 mr-12'
      }`}>
        <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
        
        {message.actions && message.actions.length > 0 && (
          <div className="mt-4 space-y-2">
            <Separator className="my-3" />
            {message.actions.map((action, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                onClick={() => onActionClick(action)}
                className="mr-2 mb-2"
              >
                {action.label || action.type}
              </Button>
            ))}
          </div>
        )}
        
        <div className="mt-3">
          <span className="text-xs opacity-70">
            {message.timestamp.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

const TypingIndicator = () => {
  return (
    <div className="flex justify-start">
      <div className="bg-gray-100 px-6 py-4 rounded-2xl mr-12">
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
          <span className="text-sm text-gray-500">Assistant is thinking...</span>
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;